/* OEO-owned native bundle. No framework or external service dependency. */
(() => {
  'use strict';

  const sections = ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'];
  const validId = value => typeof value === 'string' && /^[1-9]\d*$/.test(value);
  /** @param {{id:string,available:boolean,price:number}[]} cards @param {string} source @param {string} root @param {string} pathname */
  function buildCartRequest(cards, source, root, pathname) {
    if (!Array.isArray(cards) || cards.length < 1 || cards.length > 3 || !validId(source) ||
        !cards.every(card => card && validId(card.id) && card.available === true && Number.isSafeInteger(card.price) && card.price >= 0) ||
        new Set(cards.map(card => card.id)).size !== cards.length ||
        typeof root !== 'string' || !/^\/(?:[a-zA-Z0-9_-]+\/)*$/.test(root) ||
        typeof pathname !== 'string' || !pathname.startsWith('/') || pathname.startsWith('//') || /[\\?#\s]/.test(pathname)) throw new Error('Invalid cart request');
    // Private line properties are attribution, never proof of discount eligibility.
    const tier = cards.length === 3 ? '15' : cards.length === 2 ? '10' : '0';
    const attribution = {_oeo_bundle_source:source, _oeo_bundle_tier:tier, _oeo_bundle_version:'2026-09-10-v1'};
    return {url:root + 'cart/add.js', cartUrl:root + 'cart.js', payload:{
      items:cards.map(card => ({id:card.id,quantity:1,properties:{...attribution}})),
      sections:[...sections], sections_url:pathname
    }};
  }
  /** @param {string} handle @param {{name:string,value:string}[]} options @param {string} country */
  function buildVariantRequest(handle, options, country) {
    if (typeof handle !== 'string' || !handle || handle !== handle.trim() ||
        typeof country !== 'string' || !/^[A-Z]{2}$/.test(country) ||
        !Array.isArray(options) || options.length < 1 || options.length > 3 ||
        !options.every(option => option && typeof option.name === 'string' && option.name && typeof option.value === 'string' && option.value) ||
        new Set(options.map(option => option.name)).size !== options.length) throw new Error('Invalid option request');
    return {query: `query OeoBundleVariant($handle: String!, $options: [SelectedOptionInput!]!, $country: CountryCode!) @inContext(country: $country) {
      product(handle: $handle) {
        variantBySelectedOptions(selectedOptions: $options, ignoreUnknownOptions: false, caseInsensitiveMatch: false) {
          id availableForSale price { amount currencyCode } selectedOptions { name value }
        }
      }
    }`, variables:{handle, options:options.map(({name,value}) => ({name,value})), country}};
  }
  async function requestJSON(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {...options, redirect:'error', signal:controller.signal});
      const data = await response.json();
      return {response, data};
    } finally {
      clearTimeout(timer);
    }
  }
  function initialize(section) {
    if (section.dataset.ready) return;
    const cards = [...section.querySelectorAll('[data-bundle-card]')].map(element => ({
      element, checkbox: element.querySelector('[data-include]'), source: element.hasAttribute('data-source-card'),
      id: element.dataset.variantId, price: Number(element.dataset.price),
      available: element.dataset.available === 'true'
    }));
    const button = section.querySelector('[data-add]');
    // Capture source identity once; mutable DOM state cannot remove/reassign it.
    const source = section.dataset.source;
    const selectedCards = () => cards.filter(card => card.source || card.checkbox.checked);
    function selectionRequest(selected) {
      if (cards.filter(card => card.source).length !== 1) throw new Error('Invalid bundle source');
      return buildCartRequest(selected, source, window.Shopify?.routes?.root, window.location.pathname);
    }
    let busy = false;
    let needsCartReview = false;
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: section.dataset.currency
    });
    const money = value => formatter.format(value / 100);
    cards.forEach(card => {
      if (validId(card.id) && Number.isSafeInteger(card.price) && card.price >= 0) {
        card.element.querySelector('[data-card-price]').textContent = money(card.price);
      }
    });
    function update() {
      cards.filter(card => card.source).forEach(card => { card.checkbox.checked = true; card.checkbox.disabled = true; });
      const selected = selectedCards();
      let valid = true;
      try { selectionRequest(selected); }
      catch { valid = false; }
      const total = selected.reduce((sum, card) => sum + card.price, 0);
      const discount = valid && selected.length === 3 ? 15 : valid && selected.length === 2 ? 10 : 0;
      let thirdAvailable = false;
      if (discount === 10 && cards.length === 3) {
        try { selectionRequest(cards); thirdAvailable = true; }
        catch { /* Do not nudge toward an unconfirmed or unavailable third card. */ }
      }
      const badge = section.querySelector('[data-save-badge]');
      badge.textContent = discount ? `Save ${discount}%` : '';
      badge.hidden = !discount;
      const count = section.querySelector('[data-selection-count]');
      if (discount === 15) count.textContent = 'Set complete: your 15% saving is unlocked';
      else if (discount === 10) count.textContent = 'Pair complete: your 10% saving is unlocked.' + (thirdAvailable ? ' Add the third piece for 15%.' : '');
      else if (selected.length === 1 && valid) count.textContent = '1 item selected. Add one piece to unlock 10% off both.';
      else count.textContent = `${selected.length} ${selected.length === 1 ? 'item' : 'items'} selected`;
      // Shopify truncates percentage discounts per unit, not on the subtotal.
      const saving = discount ? selected.reduce((sum, card) => sum + Math.floor(card.price * discount / 100), 0) : 0;
      section.querySelector('[data-total]').textContent = valid ? money(total) : 'Unavailable';
      const savings = section.querySelector('[data-savings]');
      savings.replaceChildren();
      savings.hidden = !discount;
      if (discount) {
        const label = document.createElement('dt');
        label.textContent = `You save (${discount}%)`;
        const value = document.createElement('dd');
        value.dataset.savingsValue = '';
        value.textContent = `−${money(saving)}`;
        savings.append(label, value);
      }
      section.querySelector('[data-final-label]').textContent = discount ? 'Estimated bundle total' : 'Estimated total';
      section.querySelector('[data-estimated-total]').textContent = valid ? money(total - saving) : 'Unavailable';
      button.textContent = discount === 15 ? `Add all 3 and save ${money(saving)}` :
        discount === 10 ? `Add both and save ${money(saving)}` : 'Add selected to cart';
      button.disabled = busy || needsCartReview || !selected.length || !valid;
    }
    async function resolve(card) {
      const revision = card.revision = (card.revision || 0) + 1;
      const options = [...card.element.querySelectorAll('select')].map(select => ({name:select.dataset.optionName, value:select.value}));

      card.id = '';
      card.available = false;
      card.element.dataset.variantId = '';
      const status = card.element.querySelector('[data-card-status]');
      const link = card.element.querySelector('[data-product-link]');
      const url = new URL(link.href);
      url.searchParams.delete('variant');
      link.href = url.pathname + url.search;
      status.textContent = 'Checking options…';
      update();
      try {
        const {response, data} = await requestJSON('/api/2026-07/graphql.json', {
          method: 'POST', credentials: 'omit', headers: {'Content-Type':'application/json'},
          body: JSON.stringify(buildVariantRequest(card.element.dataset.handle, options, window.Shopify?.country))
        });
        if (!response.ok) throw new Error('Unable to check this option.');
        if (revision !== card.revision) return;
        const variant = data.data?.product?.variantBySelectedOptions;
        const amount = variant?.price?.amount;
        if (data.errors?.length || !variant || variant.availableForSale !== true ||
            !/^gid:\/\/shopify\/ProductVariant\/[1-9]\d*$/.test(variant.id) ||
            typeof amount !== 'string' || !/^\d+(\.\d{1,2})?$/.test(amount) ||
            !Number.isSafeInteger(Math.round(Number(amount) * 100)) ||
            variant.price.currencyCode !== section.dataset.currency ||
            !Array.isArray(variant.selectedOptions) || variant.selectedOptions.length !== options.length ||
            !options.every(option => variant.selectedOptions.some(value => value.name === option.name && value.value === option.value))) {
          throw new Error('This combination is unavailable.');
        }
        card.id = variant.id.split('/').pop();
        card.price = Math.round(Number(amount) * 100);
        card.available = true;
        card.element.dataset.variantId = card.id;
        card.element.querySelector('[data-card-price]').textContent = money(card.price);
        url.searchParams.set('variant',card.id);
        link.href = url.pathname + url.search;
        status.textContent = 'Available';
      } catch {
        if (revision !== card.revision) return;
        status.textContent = 'Unable to confirm this combination. It may be unavailable. Choose another option or visit the product page.';
        card.element.querySelector('[data-card-price]').textContent = 'Price unavailable';
      }
      update();
    }
    function setControlsDisabled(disabled) {
      cards.forEach(card => {
        card.checkbox.disabled = disabled || card.source;
        if (card.source) card.checkbox.checked = true;
        card.element.querySelectorAll('select').forEach(control => { control.disabled = disabled; });
      });
    }
    setControlsDisabled(false);
    button.addEventListener('click', async () => {
      if (button.disabled || busy) return;
      let request;
      try { request = selectionRequest(selectedCards()); }
      catch {
        section.querySelector('[data-bundle-status]').textContent = 'Unable to add this selection. Please use the product links.';
        update();
        return;
      }
      const {payload} = request;
      busy = true;
      button.setAttribute('aria-busy', 'true');
      setControlsDisabled(true);
      update();
      const status = section.querySelector('[data-bundle-status]');
      status.textContent = 'Adding your selected pieces…';

      let added;
      let failure = '';
      let confirmed = '';
      let confirmedCart;
      const containsItems = items => Array.isArray(items) && payload.items.every(item =>
        items.some(line => String(line.variant_id ?? line.id) === item.id && Number.isInteger(line.quantity) && line.quantity >= 1));
      try {
        const {response, data} = await requestJSON(request.url, {
          method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload)
        });
        added = data;
        if (!response.ok) {
          failure = `Cart error ${response.status}: ${typeof added.description === 'string' ? added.description.slice(0, 300) : 'Unable to add the selection.'}`;
        } else if (!containsItems(added.items)) {
          failure = 'The add response could not confirm every selected piece.';
        }
      } catch {
        // An interrupted mutation may have succeeded. Never retry automatically.
        failure = 'The add request could not be confirmed.';
      }
      try {
        // Ajax add returns added lines, NOT the full cart. Also reconcile on 422:
        // a rejected/partial add is not proof that the cart remained unchanged.
        const {response:cartResponse, data:cart} = await requestJSON(request.cartUrl, {credentials:'same-origin',cache:'no-store'});
        if (!cartResponse.ok || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 ||
            !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 ||
            cart.currency !== section.dataset.currency || !Array.isArray(cart.items) ||
            !cart.items.every(line => Number.isSafeInteger(line.quantity) && line.quantity > 0) ||
            cart.items.reduce((sum, line) => sum + line.quantity, 0) !== cart.item_count) throw new Error('Invalid cart');
        confirmedCart = cart;
        confirmed = `Cart confirmed: ${cart.item_count} items, ${money(cart.total_price)}. The cart's prices and discounts are authoritative.`;
        if (!failure && !containsItems(cart.items)) failure = 'The cart does not confirm every selected piece.';
      } catch {
        failure ||= 'The cart could not be confirmed after the add request.';
      }
      if (!failure) {
        try {
          // Add-time sections can predate the reconciled cart. Read, never re-add.
          const drawer = document.querySelector('cart-drawer#CartDrawer');
          const sectionId = drawer?.getAttribute('data-section-id') || '';
          const pubsub = Reflect.get(window, 'FoxTheme')?.pubsub;
          const showDrawer = drawer && Reflect.get(drawer, 'show');
          const sleek = Boolean(drawer && /^[a-zA-Z0-9_-]{1,200}$/.test(sectionId) &&
            typeof pubsub?.publish === 'function' && typeof pubsub?.PUB_SUB_EVENTS?.cartUpdate === 'string' &&
            typeof showDrawer === 'function');
          const requestedSections = sleek ? [sectionId] : sections;
          // Sleek intentionally omits its drawer on /cart; render it at the locale root.
          const sectionContext = sleek ? request.cartUrl.replace(/cart\.js$/, '') : request.cartUrl.slice(0, -3);
          const {response, data} = await requestJSON(sectionContext + '?sections=' + requestedSections.map(encodeURIComponent).join(','), {
            method:'GET', credentials:'same-origin', cache:'no-store'
          });
          if (!response.ok) throw new Error('Cart sections unavailable');
          if (sleek) {
            // Sleek owns drawer controls, count elements and their event bindings.
            const markup = data?.[sectionId];
            const parsed = typeof markup === 'string' ? new DOMParser().parseFromString(markup, 'text/html') : null;
            const fresh = parsed?.getElementById('CartDrawer-' + sectionId);
            if (!fresh) throw new Error('Cart section missing');
            await pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, {
              cart: {...confirmedCart, sections: {[sectionId]: fresh.innerHTML}}
            });
            showDrawer.call(drawer, button);
          } else {
            const selectors = ['#CartDrawer', '#cart-icon-bubble', '#cart-live-region-text'];
            const replacements = sections.map((id, index) => {
              const target = document.querySelector(selectors[index]);
              const markup = data?.[id];
              const parsed = typeof markup === 'string' ? new DOMParser().parseFromString(markup, 'text/html') : null;
              const fresh = parsed?.querySelector(selectors[index]);
              if (!target || !fresh) throw new Error('Cart section missing');
              return {target, fresh};
            });
            // Validate the whole display before changing any part of it.
            replacements.forEach(({target, fresh}) => target.replaceChildren(...fresh.childNodes));
          }
        } catch {
          failure = 'Cart display could not fully refresh.';
        }
      }
      needsCartReview = Boolean(failure);
      status.textContent = failure ? `${failure} ${confirmed} View cart and review it before adding again.` : `Added. ${confirmed}`;
      busy = false;
      button.setAttribute('aria-busy','false');
      setControlsDisabled(false);
      update();
    });
    cards.forEach(card => {
      card.checkbox.addEventListener('change', update);
      card.element.querySelectorAll('select').forEach(select => select.addEventListener('change', () => resolve(card)));
    });
    update();
    section.dataset.ready = 'true';
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {buildVariantRequest, buildCartRequest};
  if (typeof document === 'undefined' || window.oeoBundleNativeLoaded) return;
  window.oeoBundleNativeLoaded = true;
  const pendingSections = new Set();
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (entry.target.isConnected) initialize(entry.target);
        observer.unobserve(entry.target);
        pendingSections.delete(entry.target);
      }
    });
  }) : null;
  function reconcilePresence() {
    document.documentElement.classList.toggle('oeo-bundle-native-present', Boolean(document.querySelector('[data-oeo-bundle]')));
    pendingSections.forEach(section => {
      if (!section.isConnected) {
        observer?.unobserve(section);
        pendingSections.delete(section);
      }
    });
  }
  function scan(container) {
    const targets = [...container.querySelectorAll('[data-oeo-bundle]')];
    if (container.matches?.('[data-oeo-bundle]')) targets.unshift(container);
    targets.forEach(section => {
      if (section.isConnected && !section.dataset.ready) {
        if (observer) { observer.observe(section); pendingSections.add(section); }
        else initialize(section);
      }
    });
    reconcilePresence();
  }
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', reconcilePresence);
  // Unload can precede detachment; also cover replacements without theme events.
  new MutationObserver(reconcilePresence).observe(document.documentElement, {childList:true, subtree:true});
  scan(document);
})();
